import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  computed,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import { TranslatePipe } from '@ngx-translate/core';
import { SavedViewService } from '../../services/saved-view.service';
import { SavedViewContext } from '../../models/saved-view.model';

export const SAVED_VIEW_NAME_MAX = 80;
const PANEL_WIDTH = 300;

export interface SavedViewOption {
  name: string;
  summary: string;
}

@Component({
  selector: 'app-saved-views-menu',
  imports: [MatIcon, TranslatePipe],
  templateUrl: './saved-views-menu.component.html',
  styleUrl: './saved-views-menu.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SavedViewsMenuComponent {
  private readonly savedViewService = inject(SavedViewService);

  readonly views = input<SavedViewOption[]>([]);
  readonly activeName = input<string | null>(null);
  readonly context = input.required<SavedViewContext>();
  readonly projectId = input.required<string>();
  readonly updatable = input(false);
  readonly activeDirty = input(false);
  readonly saveHeadingKey = input('savedViews.saveLabel');

  readonly viewSelected = output<string>();
  readonly viewSaved = output<string>();
  readonly viewDeleted = output<string>();
  readonly viewDeleteFailed = output<string>();
  readonly viewUpdated = output<string>();

  readonly open = signal(false);
  readonly nameInput = signal('');
  readonly alignRight = signal(false);

  readonly nameMax = SAVED_VIEW_NAME_MAX;
  readonly canSave = computed(() => this.nameInput().trim().length > 0);
  readonly atNameMax = computed(() => this.nameInput().length >= SAVED_VIEW_NAME_MAX);
  readonly viewsCount = computed(() => this.views().length);
  readonly buttonLabelKey = computed(() => (this.activeName() ? null : 'savedViews.button'));
  readonly showUpdate = computed(() => this.updatable() && this.activeName() !== null && this.activeDirty());

  private readonly root = viewChild<ElementRef<HTMLElement>>('root');
  private readonly trigger = viewChild<ElementRef<HTMLButtonElement>>('trigger');

  toggle(): void {
    if (!this.open()) {
      this.alignRight.set(this.wouldOverflowRight());
    }
    this.open.update((v) => !v);
  }

  close(): void {
    this.open.set(false);
    this.nameInput.set('');
  }

  onSelect(name: string): void {
    this.viewSelected.emit(name);
    this.closeAndRestoreFocus();
  }

  onDelete(event: Event, name: string): void {
    event.stopPropagation();
    this.savedViewService.delete(this.projectId(), this.context(), name).subscribe({
      next: () => this.viewDeleted.emit(name),
      error: () => this.viewDeleteFailed.emit(name),
    });
  }

  onNameInput(event: Event): void {
    this.nameInput.set((event.target as HTMLInputElement).value);
  }

  onSaveKey(event: KeyboardEvent): void {
    if (event.key === 'Enter' && this.canSave()) {
      event.preventDefault();
      this.save();
    }
  }

  save(): void {
    const name = this.nameInput().trim();
    if (!name) return;
    this.viewSaved.emit(name);
    this.closeAndRestoreFocus();
  }

  update(): void {
    const name = this.activeName();
    if (!name) return;
    this.viewUpdated.emit(name);
    this.closeAndRestoreFocus();
  }

  private closeAndRestoreFocus(): void {
    const focusWasInside = this.root()?.nativeElement.contains(document.activeElement) ?? false;
    if (focusWasInside) {
      this.trigger()?.nativeElement.focus();
    }
    this.close();
  }

  private wouldOverflowRight(): boolean {
    const trigger = this.trigger()?.nativeElement;
    if (!trigger) return false;
    return trigger.getBoundingClientRect().left + PANEL_WIDTH > document.documentElement.clientWidth;
  }

  @HostListener('document:click', ['$event'])
  handleDocumentClick(event: MouseEvent): void {
    if (!this.open()) return;
    const root = this.root()?.nativeElement;
    if (root && !root.contains(event.target as Node)) {
      this.close();
    }
  }

  @HostListener('document:keydown.escape')
  handleEscape(): void {
    if (this.open()) {
      this.closeAndRestoreFocus();
    }
  }
}
